import { useStaticQueryWithPreview } from "@tedgustaf/gatsby-plugin-sanity-preview";
import { graphql, useStaticQuery } from "gatsby";
import React, { useContext } from "react";
import linkArrow from "../assets/images/link-arrow.svg";
import { SanityNode } from "../config/build/models/node";
import { PageContext } from "../context/PageContext";
import useTranslations from "../hooks/useTranslations";
import { SanityImageSourceExt } from "../models/shared/SanityImageSourceExt";
import { pestIdentifierPreviewQuery } from "../preview/staticQueries/pestList";
import { isNullOrWhiteSpace, replaceWithUnicode } from "../shared/string";
import { compareLanguages } from "../utilities/routes/utilities";
import LinkWithArrow from "./LinkWithArrow";
import { PortableTextColorThemeProps } from "./PortableText/PortableText";
import SanityImage from "./SanityImage";
import SanityLink from "./SanityLink";

export type PestListPestIdentifier = {
  i18nLang?: string;
  slug?: {
    current: string;
  };
};

export type PestIdentifierQuery = {
  allPestIdentifiers?: {
    nodes?: PestListPestIdentifier[];
  };
};

interface Pest extends SanityNode {
  thumbnail: SanityImageSourceExt;
}

export interface PestListProps {
  title?: string;
  subtitle?: string;
  portableTextColorThemeProps?: PortableTextColorThemeProps;
  pests?: Pest[];
}

const PestList = (props: PestListProps) => {
  const { title, subtitle, portableTextColorThemeProps, pests } = props;

  const translations = useTranslations();

  const {
    elementClasses: { h3Subheader: subtitleClasses = "text-subheader-primary" } = {}
  } = portableTextColorThemeProps || {};

  const pageContext = useContext(PageContext);

  const data = useStaticQueryWithPreview({
    staticQueryData: useStaticQuery<PestIdentifierQuery>(
      graphql`
        query GetPestIdentifier {
          allPestIdentifiers: allSanityPestIdentifier(
            filter: { slug: { current: { ne: null } } }
          ) {
            nodes {
              slug {
                current
              }
              i18nLang
            }
          }
        }
      `
    ),
    query: pestIdentifierPreviewQuery
  });

  const pestIdentifier = data.allPestIdentifiers?.nodes?.find(pestIdentifier =>
    compareLanguages(pestIdentifier.i18nLang, pageContext.i18nLang)
  );

  const pestIdentifierLinkInfo = {
    title: translations.allPests ?? undefined,
    reference: pestIdentifier
  };

  return (
    <aside className="flex flex-col px-0 max-w-aside-with-shadows">
      {!isNullOrWhiteSpace(title) && (
        <h2 className="text-current mb-2 px-4 md:px-6 xl:px-10">{title}</h2>
      )}
      {!isNullOrWhiteSpace(subtitle) && (
        <h3
          className={`${subtitleClasses} px-4 md:px-6 xl:px-10 text-base lg:text-xl uppercase font-bold`}
        >
          {subtitle}
        </h3>
      )}
      {/* Lots of wrappers required here to achieve justify-center with horizontal scrolling and padding-right */}
      <div className="flex justify-center mt-0">
        <div className="overflow-hidden">
          <ul className="flex overflow-x-auto pt-8 pb-12 px-4 md:px-6 xl:px-10">
            {pests?.map((pest, index) => (
              <li key={index} className="flex-auto mr-10 last:mr-0">
                <SanityLink
                  reference={pest}
                  className="
                      flex flex-col h-full card-basic py-6 px-12 group text-center bg-neutral-100 outline-none cursor-pointer
                      hover:border-brand-primary-70 hover:shadow-card-lifted hover:-translate-y-3 focus:outline-none focus:shadow-card-outline
                    "
                >
                  <div className="px-2 mb-3 flex justify-center items-center rounded-full">
                    <div className="flex w-40 h-40 justify-center items-center">
                      <SanityImage
                        className="rounded-full"
                        image={pest.thumbnail}
                        loading="lazy"
                        width={156}
                        height={156}
                      />
                    </div>
                  </div>

                  <h4 className="mb-6 text-base-lg uppercase text-brand-primary-20 font-bold">
                    {replaceWithUnicode(pest.title)}
                  </h4>

                  <div className="flex justify-center items-center mt-auto">
                    <svg className="w-4 h-4 text-brand-primary-95 card-arrow group-hover:text-brand-primary-70">
                      <use xlinkHref={linkArrow.url} />
                    </svg>
                  </div>
                </SanityLink>
              </li>
            ))}
          </ul>
          {pestIdentifier && (
            <div className="flex items-center justify-end w-full mx-auto px-4 md:px-6 lg:px-12">
              <LinkWithArrow linkInfo={pestIdentifierLinkInfo} />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default PestList;

